import React,{useState,useEffect,useRef}from'react';
import {useTranslation} from 'react-i18next';
import api from'../../services/api';
import {getCurrencySymbol} from '../../utils/locale';
import {useDrugTranslations,getManualDrugTranslation} from '../../utils/drugTranslation';
import AutoTranslatedText from './AutoTranslatedText';

interface Drug{
  id:string;
  name:string;
  genericName?:string;
  brandName?:string;
  strength?:string;
  dosageForm?:string;
  totalStock?:number;
  scheduleClass?:string;
  mrp?:number;
}

interface Props{
  value:string;
  onChange:(drugName:string,drug?:Drug)=>void;
  placeholder?:string;
  className?:string;
}

export default function DrugSearchInput({value,onChange,placeholder='Search drug name…',className=''}:Props){
  const{i18n}=useTranslation();
  const drugTranslations=useDrugTranslations();
  const[q,setQ]=useState(value||'');
  const[results,setResults]=useState<Drug[]>([]);
  const[show,setShow]=useState(false);
  const[loading,setLoading]=useState(false);
  const[selected,setSelected]=useState(false);
  const ref=useRef<HTMLDivElement>(null);

  // close on outside click
  useEffect(()=>{
    const h=(e:MouseEvent)=>{if(ref.current&&!ref.current.contains(e.target as Node))setShow(false);};
    document.addEventListener('mousedown',h);
    return()=>document.removeEventListener('mousedown',h);
  },[]);

  // search with debounce
  useEffect(()=>{
    if(selected){return;}
    if(q.length<2){setResults([]);return;}
    const t=setTimeout(async()=>{
      setLoading(true);
      try{
        const r=await api.getDrugs({search:q,take:20});
        const list=r?.data?.drugs||r?.data?.data||r?.data||[];
        setResults(Array.isArray(list)?list:[]);
        setShow(true);
      }catch{setResults([]);}
      finally{setLoading(false);}
    },300);
    return()=>clearTimeout(t);
  },[q,selected]);

  const pick=(drug:Drug)=>{
    const displayName=drug.name+(drug.strength?` ${drug.strength}`:'');
    setQ(displayName);setSelected(true);setShow(false);setResults([]);
    onChange(displayName,drug);
  };

  const handleChange=(e:React.ChangeEvent<HTMLInputElement>)=>{
    setQ(e.target.value);setSelected(false);onChange(e.target.value,undefined);
  };

  const stockColor=(s:number)=>s<=0?'text-red-600':s<=10?'text-amber-600':'text-green-600';
  const schedBadge=(cls:string)=>{
    if(!cls)return null;
    const c:any={H:'bg-red-100 text-red-700',H1:'bg-red-100 text-red-700',X:'bg-purple-100 text-purple-700',G:'bg-slate-100 text-slate-600'};
    return<span className={`text-xs px-1.5 py-0.5 rounded font-bold ${c[cls]||'bg-slate-100 text-slate-500'}`}>Sch-{cls}</span>;
  };

  return(
    <div ref={ref} className={`relative ${className}`}>
      <div className="relative">
        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm pointer-events-none">💊</span>
        <input
          value={q}
          onChange={handleChange}
          onFocus={()=>{if(results.length>0)setShow(true);}}
          placeholder={placeholder}
          className="w-full pl-10 pr-9 py-2 border-2 border-slate-200 rounded-xl text-sm focus:outline-none focus:border-[#02357A] focus:ring-2 focus:ring-[#02357A]/10 transition-all"
        />
        <div className="absolute right-3 top-1/2 -translate-y-1/2">
          {loading
            ?<div className="w-3.5 h-3.5 border-2 border-[#02357A]/30 border-t-[#02357A] rounded-full animate-spin"/>
            :q&&<button type="button" onClick={()=>{setQ('');setSelected(false);setResults([]);onChange('',undefined);}} className="text-slate-300 hover:text-slate-600 text-lg leading-none">×</button>
          }
        </div>
      </div>

      {show&&results.length>0&&(
        <div className="absolute z-50 top-full left-0 right-0 mt-1.5 bg-white border border-slate-200 rounded-xl shadow-2xl overflow-hidden"
          style={{maxHeight:'280px',overflowY:'auto'}}>

          {/* Header */}
          <div className="px-3 py-2 bg-slate-50 border-b border-slate-100 flex justify-between items-center sticky top-0">
            <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">💊 Drug Results</span>
            <span className="text-xs text-slate-400">{results.length} found</span>
          </div>

          {/* Drug rows */}
          {results.map(drug=>(
            <div key={drug.id} onClick={()=>pick(drug)}
              className="px-3 py-2.5 hover:bg-blue-50 cursor-pointer border-b border-slate-100 last:border-0 group transition-colors">
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <span className="font-semibold text-sm text-slate-800 group-hover:text-[#02357A]"><AutoTranslatedText text={drug.name} manual={getManualDrugTranslation(drug,i18n.language,drugTranslations)}/></span>
                    {drug.strength&&<span className="text-xs bg-blue-50 text-[#02357A] px-1.5 py-0.5 rounded border border-blue-100 font-medium">{drug.strength}</span>}
                    {drug.dosageForm&&<span className="text-xs text-slate-400">{drug.dosageForm}</span>}
                    {drug.scheduleClass&&schedBadge(drug.scheduleClass)}
                  </div>
                  {drug.genericName&&<p className="text-xs text-slate-400 mt-0.5 italic">{drug.genericName}</p>}
                  {drug.brandName&&drug.brandName!==drug.name&&<p className="text-xs text-slate-300 mt-0.5">Brand: {drug.brandName}</p>}
                </div>
                <div className="text-right flex-shrink-0">
                  {drug.totalStock!=null&&(
                    <p className={`text-xs font-bold ${stockColor(drug.totalStock)}`}>
                      {drug.totalStock<=0?'Out of Stock':drug.totalStock<=10?`Low: ${drug.totalStock}`:`Stock: ${drug.totalStock}`}
                    </p>
                  )}
                  {drug.mrp&&+drug.mrp>0&&<p className="text-xs text-slate-400">MRP {getCurrencySymbol()}{drug.mrp}</p>}
                </div>
              </div>
            </div>
          ))}

          {/* No results */}
          {!loading&&results.length===0&&q.length>=2&&(
            <div className="p-5 text-center text-sm text-slate-400">No drugs found for "{q}"</div>
          )}
        </div>
      )}
    </div>
  );
}
